import { ErrorResponse, ResponseArray, ResponseObject } from '@/types/shared'
import automation from '@/utils/axios/automation'
import authSdk from '@/utils/sdk'
import axios from 'axios'
import qs from 'query-string'
import {
    BulkDeliveryPayload,
    CreateMailerTemplatePayload,
    Mailer,
    MailerBatch,
    MailerCampaign,
    MailerCampaignDetailResponse,
    MailerCampaignFilters,
    MailerCampaignFiltersInternal,
    MailerInternalSource,
    MailerInternalSourcePreview,
    MailerInternalType,
    MailerReceiver,
    MailerTemplate,
    MailerTemplateDetailResponse,
    MailerTemplateFilters,
    SendTestPayload,
    UpdateMailerCampaignPayload,
    UpdateMailerTemplatePayload,
} from '../types'

const BASE_URL = '/mailers/marketing/campaigns'

export const getMailerCampaigns = async (filters: MailerCampaignFilters) => {
    const response = await automation.get<ResponseArray<MailerCampaign>>(
        `${BASE_URL}?${qs.stringify(filters)}`
    )
    return response.data
}

export const getMailerCampaignByID = async (
    campaignId: number
): Promise<MailerCampaignDetailResponse> => {
    const response = await automation.get<
        ResponseObject<{ campaign: MailerCampaign; template: MailerTemplate }>
    >(`${BASE_URL}/${campaignId}`)
    return response.data.result
}

export const createMailerCampaign = async (
    payload: UpdateMailerCampaignPayload
) => {
    const response = await automation.post<ResponseObject<MailerCampaign>>(
        `${BASE_URL}`,
        payload
    )
    return response.data
}

export const updateMailerCampaign = async (
    campaignId: number,
    payload: UpdateMailerCampaignPayload
): Promise<ResponseObject<MailerCampaign> | ErrorResponse> => {
    const response = await automation.patch(
        `${BASE_URL}/${campaignId}`,
        payload
    )
    return response.data
}

export const deleteMailerCampaign = async (
    campaignId: number
): Promise<ResponseObject<MailerCampaign> | ErrorResponse> => {
    const response = await automation.delete(`${BASE_URL}/${campaignId}`)
    return response.data
}

export const getMailerCampaignReceivers = async (campaignId: number) => {
    const response = await automation.get<ResponseArray<MailerReceiver>>(
        `${BASE_URL}/${campaignId}/receivers`
    )
    return response.data.results || []
}

export const sendTestEmail = async (
    campaignId: number,
    payload: SendTestPayload
): Promise<ResponseObject<{ mailer: unknown }> | ErrorResponse> => {
    const response = await automation.post(
        `${BASE_URL}/${campaignId}/delivery_v2`,
        payload
    )
    return response.data
}

export const bulkDelivery = async (
    campaignId: number,
    payload: BulkDeliveryPayload
): Promise<ResponseObject<MailerCampaign> | ErrorResponse> => {
    const response = await automation.post(
        `${BASE_URL}/${campaignId}/bulk_delivery_v2`,
        payload
    )
    return response.data
}

export const getMailerCampaignMailers = async (campaignId: number) => {
    const response = await automation.get<ResponseArray<Mailer>>(
        `${BASE_URL}/${campaignId}/mailers`
    )
    return response.data.results || []
}

export const getMailerCampaignBatches = async (campaignId: number) => {
    const response = await automation.get<{ results: MailerBatch[] }>(
        `${BASE_URL}/${campaignId}/batches`
    )
    return response.data.results || []
}

// Templates
const TEMPLATES_URL = '/mailers/marketing/templates'

export const getMailerTemplates = async (
    filters: MailerTemplateFilters = {}
) => {
    const response = await automation.get<ResponseArray<MailerTemplate>>(
        `${TEMPLATES_URL}?${qs.stringify(filters)}`
    )
    return response.data
}

export const getMailerTemplateByID = async (
    templateId: number
): Promise<MailerTemplateDetailResponse> => {
    const response = await automation.get<ResponseObject<MailerTemplate>>(
        `${TEMPLATES_URL}/${templateId}`
    )
    return response.data.result
}

export const createMailerTemplate = async (
    payload: CreateMailerTemplatePayload
): Promise<ResponseObject<MailerTemplate> | ErrorResponse> => {
    const response = await automation.post(`${TEMPLATES_URL}`, payload)
    return response.data
}

export const updateMailerTemplate = async (
    templateId: number,
    payload: UpdateMailerTemplatePayload
): Promise<ResponseObject<MailerTemplate> | ErrorResponse> => {
    const response = await automation.patch(
        `${TEMPLATES_URL}/${templateId}`,
        payload
    )
    return response.data
}

export const deleteMailerTemplate = async (
    templateId: number
): Promise<ResponseObject<MailerTemplate> | ErrorResponse> => {
    const response = await automation.delete(`${TEMPLATES_URL}/${templateId}`)
    return response.data
}

// Test SMTP Connection
export interface TestSmtpPayload {
    smtp: {
        user_name: string
        password: string
    }
}

export interface TestSmtpResponse {
    message: string
    smtp: {
        address: string
        port: number
        user_name: string
    }
}

// Isolated axios instance for SMTP test - completely separate, no interceptors
const smtpTestAxios = axios.create({
    timeout: 30000,
})

// Flag to prevent concurrent SMTP test calls
let isSmtpTestInProgress = false

export const testSmtpConnection = async (
    payload: TestSmtpPayload
): Promise<TestSmtpResponse> => {
    // Prevent concurrent calls
    if (isSmtpTestInProgress) {
        throw new Error('SMTP test already in progress')
    }

    isSmtpTestInProgress = true

    try {
        const token = authSdk.getAccessToken()
        const response = await smtpTestAxios.post<
            ResponseObject<TestSmtpResponse>
        >(
            `${import.meta.env.APP_AUTOMATION_API_URL}${BASE_URL}/test_smtp`,
            payload,
            {
                headers: {
                    Authorization: token ? `Bearer ${token}` : '',
                    'Content-Type': 'application/json',
                },
            }
        )
        return response.data.result || response.data
    } finally {
        isSmtpTestInProgress = false
    }
}

// Internal Sources
const INTERNAL_SOURCES_URL = '/mailers/marketing/internal_sources'

export const getInternalSources = async () => {
    const response =
        await automation.get<ResponseArray<MailerInternalSource>>(
            INTERNAL_SOURCES_URL
        )
    return response.data.results || []
}

export const getInternalSourcePreview = async (
    sourceId: number
): Promise<MailerInternalSourcePreview> => {
    const response = await automation.get<
        ResponseObject<MailerInternalSourcePreview>
    >(`${INTERNAL_SOURCES_URL}/${sourceId}/preview`)
    return response.data.result
}

// Preview filters - get receivers based on dynamic filters
export interface PreviewFiltersPayload {
    internal_type: MailerInternalType
    filters: MailerCampaignFiltersInternal
    page?: number
    page_size?: number
}

export const previewInternalFilters = async (
    payload: PreviewFiltersPayload
) => {
    const response = await automation.post<
        ResponseArray<MailerReceiver & { customer_id?: number }>
    >(`${INTERNAL_SOURCES_URL}/preview_filters`, payload)
    return response.data
}
